/**
 * FlexEngine — Built-in Exercise Database
 *
 * 40 exercises covering all major movement patterns.
 * Muscle allocations follow the fractional synergy model:
 *   1.0 = primary actor | 0.5 = secondary synergist
 *
 * Rest times follow the time-bounding constraint:
 *   Compound multi-joint: 90–120 s
 *   Isolation:            60 s
 */

import type { Exercise } from '@/types';

// ── ANTERIOR (Push) DAY — 22 exercises ────────────────────────

const anteriorExercises: Exercise[] = [
  // ── Horizontal Push ──────────────────────────────────────────
  {
    id: 'ex_bp_barbell',
    name: 'Barbell Bench Press',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
      { muscle: 'triceps',    fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_bp_incline_barbell',
    name: 'Incline Barbell Bench Press',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
      { muscle: 'triceps',    fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_bp_db',
    name: 'Dumbbell Bench Press',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
      { muscle: 'triceps',    fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_bp_incline_db',
    name: 'Incline Dumbbell Press',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
      { muscle: 'triceps',    fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_chest_fly_cable',
    name: 'Cable Chest Fly',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_chest_fly_db',
    name: 'Dumbbell Pec Fly',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_pushup',
    name: 'Push-Up',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
      { muscle: 'triceps',    fraction: 0.5 },
      { muscle: 'core',       fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Vertical Push ─────────────────────────────────────────────
  {
    id: 'ex_ohp_barbell',
    name: 'Barbell Overhead Press',
    category: 'compound',
    pattern: 'vertical_push',
    splitDay: 'anterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'front_delt',   fraction: 1.0 },
      { muscle: 'lateral_delt', fraction: 0.5 },
      { muscle: 'triceps',      fraction: 0.5 },
      { muscle: 'traps',        fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_ohp_db',
    name: 'Dumbbell Shoulder Press',
    category: 'compound',
    pattern: 'vertical_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'front_delt',   fraction: 1.0 },
      { muscle: 'lateral_delt', fraction: 0.5 },
      { muscle: 'triceps',      fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_lateral_raise_db',
    name: 'Dumbbell Lateral Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'lateral_delt', fraction: 1.0 },
      { muscle: 'traps',        fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_lateral_raise_cable',
    name: 'Cable Lateral Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'lateral_delt', fraction: 1.0 },
      { muscle: 'traps',        fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Triceps Isolation ─────────────────────────────────────────
  {
    id: 'ex_tri_pushdown_cable',
    name: 'Cable Triceps Pushdown',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'triceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_tri_overhead_cable',
    name: 'Overhead Cable Triceps Extension',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'triceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_skull_crusher',
    name: 'EZ-Bar Skull Crusher',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['ez_bar'],
    muscles: [
      { muscle: 'triceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_dips',
    name: 'Dips (Triceps)',
    category: 'compound',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'triceps',    fraction: 1.0 },
      { muscle: 'chest',      fraction: 0.5 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Quads ─────────────────────────────────────────────────────
  {
    id: 'ex_squat_barbell',
    name: 'Barbell Back Squat',
    category: 'compound',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'quads',     fraction: 1.0 },
      { muscle: 'glutes',    fraction: 0.5 },
      { muscle: 'core',      fraction: 0.5 },
      { muscle: 'hamstrings',fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_leg_press',
    name: 'Leg Press',
    category: 'compound',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['machine'],
    muscles: [
      { muscle: 'quads',  fraction: 1.0 },
      { muscle: 'glutes', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_leg_extension',
    name: 'Leg Extension',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'legs',
    equipment: ['machine'],
    muscles: [
      { muscle: 'quads', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_lunge_db',
    name: 'Dumbbell Walking Lunge',
    category: 'compound',
    pattern: 'lunge',
    splitDay: 'legs',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'quads',      fraction: 1.0 },
      { muscle: 'glutes',     fraction: 0.5 },
      { muscle: 'hamstrings', fraction: 0.5 },
      { muscle: 'core',       fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_hack_squat',
    name: 'Hack Squat',
    category: 'compound',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['machine'],
    muscles: [
      { muscle: 'quads',  fraction: 1.0 },
      { muscle: 'glutes', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Core ──────────────────────────────────────────────────────
  {
    id: 'ex_ab_wheel',
    name: 'Ab Wheel Rollout',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'core',        fraction: 1.0 },
      { muscle: 'hip_flexors', fraction: 0.5 },
      { muscle: 'lats',        fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_cable_crunch',
    name: 'Cable Crunch',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'core', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_hanging_knee_raise',
    name: 'Hanging Knee Raise',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'core',        fraction: 1.0 },
      { muscle: 'hip_flexors', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_hanging_leg_raise',
    name: 'Hanging Leg Raise',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'core',        fraction: 1.0 },
      { muscle: 'hip_flexors', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_plank',
    name: 'Plank',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'core',        fraction: 1.0 },
      { muscle: 'hip_flexors', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_crunch_floor',
    name: 'Crunch',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'core', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  // ── Additional Squat / Lunge Variants ────────────────────────
  {
    id: 'ex_front_squat',
    name: 'Barbell Front Squat',
    category: 'compound',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'quads',  fraction: 1.0 },
      { muscle: 'glutes', fraction: 0.5 },
      { muscle: 'core',   fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_goblet_squat',
    name: 'Goblet Squat',
    category: 'compound',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'quads',  fraction: 1.0 },
      { muscle: 'glutes', fraction: 0.5 },
      { muscle: 'core',   fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_bulgarian_split_squat',
    name: 'Bulgarian Split Squat',
    category: 'compound',
    pattern: 'lunge',
    splitDay: 'legs',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'quads',      fraction: 1.0 },
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 0.5 },
      { muscle: 'core',       fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_reverse_lunge_db',
    name: 'Dumbbell Reverse Lunge',
    category: 'compound',
    pattern: 'lunge',
    splitDay: 'legs',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'quads',      fraction: 1.0 },
      { muscle: 'glutes',     fraction: 0.5 },
      { muscle: 'hamstrings', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_step_up_db',
    name: 'Dumbbell Step-Up',
    category: 'compound',
    pattern: 'lunge',
    splitDay: 'legs',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'quads',  fraction: 1.0 },
      { muscle: 'glutes', fraction: 1.0 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_sumo_squat_db',
    name: 'Dumbbell Sumo Squat',
    category: 'compound',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'quads',  fraction: 1.0 },
      { muscle: 'glutes', fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Shoulder Isolation ─────────────────────────────
  {
    id: 'ex_front_raise_db',
    name: 'Dumbbell Front Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'front_delt', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_arnold_press',
    name: 'Arnold Press',
    category: 'compound',
    pattern: 'vertical_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'front_delt',   fraction: 1.0 },
      { muscle: 'lateral_delt', fraction: 1.0 },
      { muscle: 'triceps',      fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_upright_row_barbell',
    name: 'Barbell Upright Row',
    category: 'compound',
    pattern: 'vertical_pull',
    splitDay: 'anterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'lateral_delt', fraction: 1.0 },
      { muscle: 'traps',        fraction: 1.0 },
      { muscle: 'biceps',       fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Chest Variants ─────────────────────────────────
  {
    id: 'ex_decline_bp_barbell',
    name: 'Decline Barbell Bench Press',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'triceps',    fraction: 0.5 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_close_grip_bench',
    name: 'Close-Grip Bench Press',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'triceps',    fraction: 1.0 },
      { muscle: 'chest',      fraction: 0.5 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Glutes / Hip Isolation ────────────────────────────────────
  {
    id: 'ex_glute_bridge_bw',
    name: 'Glute Bridge',
    category: 'isolation',
    pattern: 'hip_hinge',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_hip_abduction_machine',
    name: 'Hip Abduction Machine',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'glutes', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_hip_adduction_machine',
    name: 'Hip Adduction Machine',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'anterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'glutes', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Chest Isolation ────────────────────────────────
  {
    id: 'ex_incline_db_fly',
    name: 'Incline Dumbbell Fly',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_cable_crossover_high_low',
    name: 'Cable Crossover (High to Low)',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_cable_crossover_low_high',
    name: 'Cable Crossover (Low to High)',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Landmine ──────────────────────────────────────────────────
  {
    id: 'ex_landmine_press',
    name: 'Landmine Press',
    category: 'compound',
    pattern: 'vertical_push',
    splitDay: 'anterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'front_delt',   fraction: 1.0 },
      { muscle: 'chest',        fraction: 0.5 },
      { muscle: 'triceps',      fraction: 0.5 },
      { muscle: 'core',         fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Quad Isolation ──────────────────────────────────
  {
    id: 'ex_sissy_squat',
    name: 'Sissy Squat',
    category: 'isolation',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'quads', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Core ───────────────────────────────────────────
  {
    id: 'ex_bicycle_crunch',
    name: 'Bicycle Crunch',
    category: 'isolation',
    pattern: 'core_rotation',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'core', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_side_plank',
    name: 'Side Plank',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'anterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'core',        fraction: 1.0 },
      { muscle: 'hip_flexors', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_pallof_press',
    name: 'Pallof Press',
    category: 'isolation',
    pattern: 'core_rotation',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'core', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_cable_front_raise',
    name: 'Cable Front Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'front_delt', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Planet Fitness / Big-Box Machine staples ───────────────────
  {
    id: 'ex_chest_press_machine',
    name: 'Chest Press Machine',
    category: 'compound',
    pattern: 'horizontal_push',
    splitDay: 'anterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
      { muscle: 'triceps',    fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_shoulder_press_machine',
    name: 'Shoulder Press Machine',
    category: 'compound',
    pattern: 'vertical_push',
    splitDay: 'anterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'front_delt',   fraction: 1.0 },
      { muscle: 'lateral_delt', fraction: 0.5 },
      { muscle: 'triceps',      fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_pec_deck',
    name: 'Pec Deck / Chest Fly Machine',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'chest',      fraction: 1.0 },
      { muscle: 'front_delt', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_tricep_machine',
    name: 'Triceps Dip Machine',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'anterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'triceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_smith_squat',
    name: 'Smith Machine Squat',
    category: 'compound',
    pattern: 'squat',
    splitDay: 'legs',
    equipment: ['machine'],
    muscles: [
      { muscle: 'quads',     fraction: 1.0 },
      { muscle: 'glutes',    fraction: 0.5 },
      { muscle: 'hamstrings',fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
];

// ── POSTERIOR (Pull) DAY — 18 exercises ───────────────────────

const posteriorExercises: Exercise[] = [
  // ── Vertical Pull ─────────────────────────────────────────────
  {
    id: 'ex_pullup',
    name: 'Pull-Up',
    category: 'compound',
    pattern: 'vertical_pull',
    splitDay: 'posterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'biceps',    fraction: 0.5 },
      { muscle: 'rhomboids', fraction: 0.5 },
      { muscle: 'rear_delt', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_lat_pulldown',
    name: 'Lat Pulldown',
    category: 'compound',
    pattern: 'vertical_pull',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'biceps',    fraction: 0.5 },
      { muscle: 'rhomboids', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_straight_arm_pulldown',
    name: 'Straight-Arm Pulldown',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'lats', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Horizontal Pull ───────────────────────────────────────────
  {
    id: 'ex_row_barbell',
    name: 'Barbell Bent-Over Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'lats',       fraction: 1.0 },
      { muscle: 'rhomboids',  fraction: 1.0 },
      { muscle: 'traps',      fraction: 0.5 },
      { muscle: 'rear_delt',  fraction: 0.5 },
      { muscle: 'biceps',     fraction: 0.5 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_row_db',
    name: 'Single-Arm Dumbbell Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 0.5 },
      { muscle: 'rear_delt', fraction: 0.5 },
      { muscle: 'biceps',    fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_row_cable_seated',
    name: 'Seated Cable Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 1.0 },
      { muscle: 'traps',     fraction: 0.5 },
      { muscle: 'biceps',    fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_chest_supported_row',
    name: 'Chest-Supported Dumbbell Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'rhomboids', fraction: 1.0 },
      { muscle: 'lats',      fraction: 0.5 },
      { muscle: 'rear_delt', fraction: 0.5 },
      { muscle: 'traps',     fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_face_pull',
    name: 'Cable Face Pull',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'rear_delt', fraction: 1.0 },
      { muscle: 'traps',     fraction: 0.5 },
      { muscle: 'rhomboids', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Hip Hinge / Hamstrings / Glutes ───────────────────────────
  {
    id: 'ex_deadlift',
    name: 'Conventional Deadlift',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'lower_back', fraction: 0.5 },
      { muscle: 'traps',      fraction: 0.5 },
      { muscle: 'quads',      fraction: 0.5 },
      { muscle: 'core',       fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_rdl',
    name: 'Romanian Deadlift',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_rdl_db',
    name: 'Dumbbell Romanian Deadlift',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_hip_thrust',
    name: 'Barbell Hip Thrust',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 0.5 },
      { muscle: 'core',       fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_leg_curl_lying',
    name: 'Lying Leg Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_leg_curl_seated',
    name: 'Seated Leg Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Biceps ────────────────────────────────────────────────────
  {
    id: 'ex_curl_barbell',
    name: 'Barbell Bicep Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'biceps',   fraction: 1.0 },
      { muscle: 'forearms', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_curl_db_incline',
    name: 'Incline Dumbbell Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'biceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_curl_cable',
    name: 'Cable Bicep Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'biceps',   fraction: 1.0 },
      { muscle: 'forearms', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_hammer_curl',
    name: 'Dumbbell Hammer Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'biceps',   fraction: 1.0 },
      { muscle: 'forearms', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Calves ────────────────────────────────────────────────────
  {
    id: 'ex_calf_raise_standing',
    name: 'Standing Calf Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'calves', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_calf_raise_seated',
    name: 'Seated Calf Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'calves', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  // ── Additional Hip Hinge / Glute ─────────────────────────────
  {
    id: 'ex_sumo_deadlift',
    name: 'Sumo Deadlift',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'quads',      fraction: 0.5 },
      { muscle: 'lower_back', fraction: 0.5 },
      { muscle: 'core',       fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_trap_bar_deadlift',
    name: 'Trap Bar Deadlift',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'quads',      fraction: 0.5 },
      { muscle: 'lower_back', fraction: 0.5 },
      { muscle: 'traps',      fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_good_morning',
    name: 'Good Morning',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'lower_back', fraction: 1.0 },
      { muscle: 'glutes',     fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_cable_pull_through',
    name: 'Cable Pull-Through',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 0.5 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_cable_glute_kickback',
    name: 'Cable Glute Kickback',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Vertical Pull ───────────────────────────────────
  {
    id: 'ex_chinup',
    name: 'Chin-Up',
    category: 'compound',
    pattern: 'vertical_pull',
    splitDay: 'posterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'biceps',    fraction: 1.0 },
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_neutral_grip_pullup',
    name: 'Neutral-Grip Pull-Up',
    category: 'compound',
    pattern: 'vertical_pull',
    splitDay: 'posterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'biceps',    fraction: 0.5 },
      { muscle: 'rhomboids', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_assisted_pullup_machine',
    name: 'Assisted Pull-Up Machine',
    category: 'compound',
    pattern: 'vertical_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'lats',   fraction: 1.0 },
      { muscle: 'biceps', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },

  // ── Additional Horizontal Pull ────────────────────────────────
  {
    id: 'ex_tbar_row',
    name: 'T-Bar Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'lats',       fraction: 1.0 },
      { muscle: 'rhomboids',  fraction: 1.0 },
      { muscle: 'traps',      fraction: 0.5 },
      { muscle: 'biceps',     fraction: 0.5 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Traps ─────────────────────────────────────────────────────
  {
    id: 'ex_shrug_barbell',
    name: 'Barbell Shrug',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'traps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_shrug_db',
    name: 'Dumbbell Shrug',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'traps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Rear Delt ─────────────────────────────────────────────────
  {
    id: 'ex_rear_delt_fly_db',
    name: 'Dumbbell Rear Delt Fly',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'rear_delt', fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Bicep Variants ─────────────────────────────────
  {
    id: 'ex_curl_db_standing',
    name: 'Dumbbell Bicep Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'biceps',   fraction: 1.0 },
      { muscle: 'forearms', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_curl_ezbar',
    name: 'EZ-Bar Bicep Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['ez_bar'],
    muscles: [
      { muscle: 'biceps',   fraction: 1.0 },
      { muscle: 'forearms', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_preacher_curl',
    name: 'Preacher Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'biceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_concentration_curl',
    name: 'Concentration Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'biceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Calf (non-machine) ────────────────────────────────────────
  {
    id: 'ex_calf_raise_bw',
    name: 'Bodyweight Standing Calf Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'posterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'calves', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_calf_raise_leg_press',
    name: 'Leg Press Calf Raise',
    category: 'isolation',
    pattern: 'isolation_push',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'calves', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Horizontal Pull ────────────────────────────────
  {
    id: 'ex_pendlay_row',
    name: 'Pendlay Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'lats',       fraction: 1.0 },
      { muscle: 'rhomboids',  fraction: 1.0 },
      { muscle: 'traps',      fraction: 0.5 },
      { muscle: 'rear_delt',  fraction: 0.5 },
      { muscle: 'biceps',     fraction: 0.5 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 120,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_meadows_row',
    name: 'Meadows Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 0.5 },
      { muscle: 'rear_delt', fraction: 0.5 },
      { muscle: 'biceps',    fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_landmine_row',
    name: 'Landmine Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 0.5 },
      { muscle: 'biceps',    fraction: 0.5 },
      { muscle: 'core',      fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Hamstring ──────────────────────────────────────
  {
    id: 'ex_nordic_hamstring_curl',
    name: 'Nordic Hamstring Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['bodyweight'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_glute_ham_raise',
    name: 'Glute-Ham Raise',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'glutes',     fraction: 0.5 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Rear Delt / Upper Back ─────────────────────────
  {
    id: 'ex_cable_rear_delt_fly',
    name: 'Cable Rear Delt Fly',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['cable'],
    muscles: [
      { muscle: 'rear_delt', fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_db_pullover',
    name: 'Dumbbell Pullover',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'lats',  fraction: 1.0 },
      { muscle: 'chest', fraction: 0.5 },
      { muscle: 'core',  fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Additional Bicep Variants ─────────────────────────────────
  {
    id: 'ex_spider_curl',
    name: 'Spider Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'biceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_seated_db_curl',
    name: 'Seated Dumbbell Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'biceps',   fraction: 1.0 },
      { muscle: 'forearms', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_reverse_curl',
    name: 'Reverse Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'forearms', fraction: 1.0 },
      { muscle: 'biceps',   fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_zottman_curl',
    name: 'Zottman Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'biceps',   fraction: 1.0 },
      { muscle: 'forearms', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Forearms ──────────────────────────────────────────────────
  {
    id: 'ex_wrist_curl',
    name: 'Wrist Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'forearms', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_reverse_wrist_curl',
    name: 'Reverse Wrist Curl',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['barbell'],
    muscles: [
      { muscle: 'forearms', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Functional / Loaded Carry ─────────────────────────────────
  {
    id: 'ex_farmers_carry',
    name: "Farmer's Carry",
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'traps',      fraction: 1.0 },
      { muscle: 'forearms',   fraction: 1.0 },
      { muscle: 'core',       fraction: 0.5 },
      { muscle: 'glutes',     fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },

  // ── Planet Fitness / Big-Box Machine staples (posterior) ──────
  {
    id: 'ex_row_machine',
    name: 'Machine Row',
    category: 'compound',
    pattern: 'horizontal_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'lats',      fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 1.0 },
      { muscle: 'biceps',    fraction: 0.5 },
      { muscle: 'rear_delt', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_pulldown_machine',
    name: 'Machine Lat Pulldown',
    category: 'compound',
    pattern: 'vertical_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'lats',   fraction: 1.0 },
      { muscle: 'biceps', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: true,
    isBuiltIn: true,
  },
  {
    id: 'ex_bicep_curl_machine',
    name: 'Bicep Curl Machine',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'biceps', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_smith_rdl',
    name: 'Smith Machine RDL',
    category: 'compound',
    pattern: 'hip_hinge',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'hamstrings', fraction: 1.0 },
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'lower_back', fraction: 0.5 },
    ],
    restSeconds: 90,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_glute_kickback_machine',
    name: 'Glute Kickback Machine',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'glutes',     fraction: 1.0 },
      { muscle: 'hamstrings', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_rear_delt_machine',
    name: 'Rear Delt Fly Machine (Pec Deck Reverse)',
    category: 'isolation',
    pattern: 'isolation_pull',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'rear_delt', fraction: 1.0 },
      { muscle: 'rhomboids', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  // ── Ab / Core (posterior split — often superset with pull day) ─
  {
    id: 'ex_roman_chair_situp',
    name: 'Roman Chair Sit-Up',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'core',        fraction: 1.0 },
      { muscle: 'hip_flexors', fraction: 0.5 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_ab_machine',
    name: 'Ab Crunch Machine',
    category: 'isolation',
    pattern: 'core_anti_extension',
    splitDay: 'posterior',
    equipment: ['machine'],
    muscles: [
      { muscle: 'core', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
  {
    id: 'ex_russian_twist',
    name: 'Russian Twist (Weighted)',
    category: 'isolation',
    pattern: 'core_rotation',
    splitDay: 'posterior',
    equipment: ['dumbbell'],
    muscles: [
      { muscle: 'core', fraction: 1.0 },
    ],
    restSeconds: 60,
    warmupEligible: false,
    isBuiltIn: true,
  },
];

// ── ADDITIONAL EXERCISES — Kettlebell, Band, Unilateral, Carry ─

const additionalExercises: Exercise[] = [
  // ── Kettlebell Push ───────────────────────────────────────────
  { id: 'ex_kb_press', name: 'Kettlebell Press', category: 'compound', pattern: 'vertical_push', splitDay: 'anterior', equipment: ['kettlebell'],
    muscles: [{ muscle: 'front_delt', fraction: 1.0 }, { muscle: 'lateral_delt', fraction: 0.5 }, { muscle: 'triceps', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_kb_clean_press', name: 'Kettlebell Clean & Press', category: 'compound', pattern: 'vertical_push', splitDay: 'anterior', equipment: ['kettlebell'],
    muscles: [{ muscle: 'front_delt', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }, { muscle: 'triceps', fraction: 0.5 }, { muscle: 'core', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: false, isBuiltIn: true },

  // ── Kettlebell Pull ───────────────────────────────────────────
  { id: 'ex_kb_swing', name: 'Kettlebell Swing', category: 'compound', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['kettlebell'],
    muscles: [{ muscle: 'glutes', fraction: 1.0 }, { muscle: 'hamstrings', fraction: 1.0 }, { muscle: 'core', fraction: 0.5 }, { muscle: 'traps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_kb_deadlift', name: 'Kettlebell Deadlift', category: 'compound', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['kettlebell'],
    muscles: [{ muscle: 'hamstrings', fraction: 1.0 }, { muscle: 'glutes', fraction: 1.0 }, { muscle: 'lower_back', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_kb_row', name: 'Kettlebell Single-Arm Row', category: 'compound', pattern: 'horizontal_pull', splitDay: 'posterior', equipment: ['kettlebell'],
    muscles: [{ muscle: 'lats', fraction: 1.0 }, { muscle: 'rhomboids', fraction: 0.5 }, { muscle: 'biceps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_kb_goblet_squat', name: 'Kettlebell Goblet Squat', category: 'compound', pattern: 'squat', splitDay: 'legs', equipment: ['kettlebell'],
    muscles: [{ muscle: 'quads', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }, { muscle: 'core', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_kb_snatch', name: 'Kettlebell Snatch', category: 'compound', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['kettlebell'],
    muscles: [{ muscle: 'glutes', fraction: 1.0 }, { muscle: 'hamstrings', fraction: 0.5 }, { muscle: 'traps', fraction: 0.5 }, { muscle: 'core', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: false, isBuiltIn: true },

  // ── Resistance Band ───────────────────────────────────────────
  { id: 'ex_band_pull_apart', name: 'Band Pull-Apart', category: 'isolation', pattern: 'isolation_pull', splitDay: 'posterior', equipment: ['bands'],
    muscles: [{ muscle: 'rear_delt', fraction: 1.0 }, { muscle: 'rhomboids', fraction: 0.5 }, { muscle: 'traps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_band_face_pull', name: 'Band Face Pull', category: 'isolation', pattern: 'isolation_pull', splitDay: 'posterior', equipment: ['bands'],
    muscles: [{ muscle: 'rear_delt', fraction: 1.0 }, { muscle: 'traps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_band_squat', name: 'Banded Squat', category: 'compound', pattern: 'squat', splitDay: 'anterior', equipment: ['bands'],
    muscles: [{ muscle: 'quads', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_band_lat_pulldown', name: 'Band Lat Pulldown', category: 'compound', pattern: 'vertical_pull', splitDay: 'posterior', equipment: ['bands'],
    muscles: [{ muscle: 'lats', fraction: 1.0 }, { muscle: 'biceps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_band_curl', name: 'Resistance Band Curl', category: 'isolation', pattern: 'isolation_pull', splitDay: 'posterior', equipment: ['bands'],
    muscles: [{ muscle: 'biceps', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_band_tricep_pushdown', name: 'Band Triceps Pushdown', category: 'isolation', pattern: 'isolation_push', splitDay: 'anterior', equipment: ['bands'],
    muscles: [{ muscle: 'triceps', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },

  // ── Bodyweight Progressions ───────────────────────────────────
  { id: 'ex_pike_pushup', name: 'Pike Push-Up', category: 'compound', pattern: 'vertical_push', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'front_delt', fraction: 1.0 }, { muscle: 'lateral_delt', fraction: 0.5 }, { muscle: 'triceps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_diamond_pushup', name: 'Diamond Push-Up', category: 'compound', pattern: 'horizontal_push', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'triceps', fraction: 1.0 }, { muscle: 'chest', fraction: 0.5 }, { muscle: 'front_delt', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_inverted_row', name: 'Inverted Row', category: 'compound', pattern: 'horizontal_pull', splitDay: 'posterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'lats', fraction: 1.0 }, { muscle: 'rhomboids', fraction: 0.5 }, { muscle: 'biceps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_bodyweight_squat', name: 'Bodyweight Squat', category: 'compound', pattern: 'squat', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'quads', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_pistol_squat', name: 'Pistol Squat', category: 'compound', pattern: 'squat', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'quads', fraction: 1.0 }, { muscle: 'glutes', fraction: 1.0 }, { muscle: 'core', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_hip_thrust_bw', name: 'Bodyweight Hip Thrust', category: 'isolation', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'glutes', fraction: 1.0 }, { muscle: 'hamstrings', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_l_sit', name: 'L-Sit Hold', category: 'isolation', pattern: 'core_anti_extension', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'core', fraction: 1.0 }, { muscle: 'hip_flexors', fraction: 1.0 }, { muscle: 'triceps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },

  // ── Cable Unilateral ──────────────────────────────────────────
  { id: 'ex_cable_curl_single', name: 'Single-Arm Cable Curl', category: 'isolation', pattern: 'isolation_pull', splitDay: 'posterior', equipment: ['cable'],
    muscles: [{ muscle: 'biceps', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_cable_fly_low', name: 'Cable Fly (Low to High)', category: 'isolation', pattern: 'isolation_push', splitDay: 'anterior', equipment: ['cable'],
    muscles: [{ muscle: 'chest', fraction: 1.0 }, { muscle: 'front_delt', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_cable_row_single', name: 'Single-Arm Cable Row', category: 'compound', pattern: 'horizontal_pull', splitDay: 'posterior', equipment: ['cable'],
    muscles: [{ muscle: 'lats', fraction: 1.0 }, { muscle: 'rhomboids', fraction: 0.5 }, { muscle: 'biceps', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_cable_overhead_tricep_single', name: 'Single-Arm Overhead Triceps Extension', category: 'isolation', pattern: 'isolation_push', splitDay: 'anterior', equipment: ['cable'],
    muscles: [{ muscle: 'triceps', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_cable_hip_abduction', name: 'Cable Hip Abduction', category: 'isolation', pattern: 'isolation_push', splitDay: 'posterior', equipment: ['cable'],
    muscles: [{ muscle: 'glutes', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },

  // ── Trap Bar ──────────────────────────────────────────────────
  { id: 'ex_trap_bar_carry', name: 'Trap Bar Carry', category: 'compound', pattern: 'carry', splitDay: 'posterior', equipment: ['trap_bar'],
    muscles: [{ muscle: 'traps', fraction: 1.0 }, { muscle: 'forearms', fraction: 1.0 }, { muscle: 'core', fraction: 0.5 }, { muscle: 'glutes', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: false, isBuiltIn: true },

  // ── Smith Machine ─────────────────────────────────────────────
  { id: 'ex_smith_bench', name: 'Smith Machine Bench Press', category: 'compound', pattern: 'horizontal_push', splitDay: 'anterior', equipment: ['machine'],
    muscles: [{ muscle: 'chest', fraction: 1.0 }, { muscle: 'front_delt', fraction: 0.5 }, { muscle: 'triceps', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: true, isBuiltIn: true },
  { id: 'ex_smith_ohp', name: 'Smith Machine Overhead Press', category: 'compound', pattern: 'vertical_push', splitDay: 'anterior', equipment: ['machine'],
    muscles: [{ muscle: 'front_delt', fraction: 1.0 }, { muscle: 'lateral_delt', fraction: 0.5 }, { muscle: 'triceps', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_smith_bulgarian', name: 'Smith Machine Bulgarian Split Squat', category: 'compound', pattern: 'lunge', splitDay: 'anterior', equipment: ['machine'],
    muscles: [{ muscle: 'quads', fraction: 1.0 }, { muscle: 'glutes', fraction: 1.0 }, { muscle: 'hamstrings', fraction: 0.5 }],
    restSeconds: 90, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_smith_calf_raise', name: 'Smith Machine Calf Raise', category: 'isolation', pattern: 'isolation_push', splitDay: 'posterior', equipment: ['machine'],
    muscles: [{ muscle: 'calves', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },

  // ── Lower Back Isolation ──────────────────────────────────────
  { id: 'ex_superman', name: 'Superman', category: 'isolation', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'lower_back', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true,
    formCue: 'Lie face down, lift arms and legs simultaneously. Squeeze glutes and hold for 2 seconds at the top.' },
  { id: 'ex_back_extension', name: 'Back Extension (Hyperextension)', category: 'isolation', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['machine'],
    muscles: [{ muscle: 'lower_back', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }, { muscle: 'hamstrings', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true,
    formCue: 'Hinge at the hip, not the lower spine. Control the descent. Stop parallel to the floor — avoid hyperextending.' },
  { id: 'ex_reverse_hyperextension', name: 'Reverse Hyperextension', category: 'isolation', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['machine'],
    muscles: [{ muscle: 'lower_back', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }, { muscle: 'hamstrings', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true,
    formCue: 'Keep hips on the pad, swing legs up using glutes and hamstrings. Avoid momentum — control the arc.' },
  { id: 'ex_bird_dog', name: 'Bird Dog', category: 'isolation', pattern: 'core_anti_extension', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'lower_back', fraction: 1.0 }, { muscle: 'core', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true,
    formCue: 'On hands and knees, extend opposite arm and leg. Keep hips level — no rotation. Hold 2 seconds, alternate sides.' },
  { id: 'ex_45_back_raise_bw', name: '45° Back Raise', category: 'isolation', pattern: 'hip_hinge', splitDay: 'posterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'lower_back', fraction: 1.0 }, { muscle: 'glutes', fraction: 0.5 }, { muscle: 'hamstrings', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: true, isBuiltIn: true,
    formCue: 'On a 45° GHD or Roman chair. Lower until torso is vertical, rise until parallel. Add plate for resistance.' },

  // ── More Core ─────────────────────────────────────────────────
  { id: 'ex_dead_bug', name: 'Dead Bug', category: 'isolation', pattern: 'core_anti_extension', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'core', fraction: 1.0 }, { muscle: 'hip_flexors', fraction: 0.5 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_hollow_body_hold', name: 'Hollow Body Hold', category: 'isolation', pattern: 'core_anti_extension', splitDay: 'anterior', equipment: ['bodyweight'],
    muscles: [{ muscle: 'core', fraction: 1.0 }, { muscle: 'hip_flexors', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
  { id: 'ex_wood_chop_cable', name: 'Cable Wood Chop', category: 'isolation', pattern: 'core_rotation', splitDay: 'anterior', equipment: ['cable'],
    muscles: [{ muscle: 'core', fraction: 1.0 }],
    restSeconds: 60, warmupEligible: false, isBuiltIn: true },
];

// ── Merged & Exported ─────────────────────────────────────────

export const BUILT_IN_EXERCISES: Exercise[] = [
  ...anteriorExercises,
  ...posteriorExercises,
  ...additionalExercises,
];

/** Quick lookup map: id → Exercise */
export const EXERCISE_MAP = new Map<string, Exercise>(
  BUILT_IN_EXERCISES.map((e) => [e.id, e]),
);

/** Count sanity-check (should be ~148) */
export const EXERCISE_COUNT = BUILT_IN_EXERCISES.length;
